/**
 * AdminTimeline — unified customer / lead history view.
 *
 * Consumes `GET /api/admin/customers/{id}/timeline` or `/leads/{id}/timeline`
 * (both return `{ events, count }` where each event has:
 *    { type, at, title, subtitle, ...id fields }
 *
 * Renders a vertical timeline grouped by day with a sticky day header.
 */
import React, { useEffect, useState, useMemo } from "react";
import { Sparkles, MessageSquare, PhoneIncoming, PhoneOutgoing, StickyNote, Mail, Calendar, Loader2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

const ICON_FOR = {
  lead: Sparkles,
  message: MessageSquare,
  note: StickyNote,
  email: Mail,
  appointment: Calendar,
  sms: MessageSquare,
};

const iconFor = (ev) => {
  if (ev.type === "call") {
    return ev.title?.includes("outbound") ? PhoneOutgoing : PhoneIncoming;
  }
  return ICON_FOR[ev.type] || Sparkles;
};

const dayKey = (iso) => (iso ? iso.slice(0, 10) : "unknown");

const AdminTimeline = ({ endpoint }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!endpoint) return;
    let cancelled = false;
    setLoading(true);
    api.get(endpoint)
      .then((r) => { if (!cancelled) setEvents(r.data.events || []); })
      .catch((e) => { if (!cancelled) setError(formatApiError(e)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [endpoint]);

  const grouped = useMemo(() => {
    const map = new Map();
    for (const ev of events) {
      const k = dayKey(ev.at);
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(ev);
    }
    return Array.from(map.entries()); // preserves insertion order (desc from API)
  }, [events]);

  if (loading) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-6 flex items-center gap-2 text-slate-500" data-testid="admin-timeline-loading">
        <Loader2 size={14} className="animate-spin" /> Loading timeline…
      </div>
    );
  }
  if (error) {
    return <p className="text-sm text-rose-600" data-testid="admin-timeline-error">{error}</p>;
  }
  if (events.length === 0) {
    return (
      <div className="rounded-2xl border border-dashed border-slate-200 bg-white p-8 text-center" data-testid="admin-timeline-empty">
        <Sparkles size={20} className="mx-auto text-slate-300" />
        <p className="mt-2 text-sm text-slate-500">No activity recorded yet.</p>
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-6" data-testid="admin-timeline">
      <div className="space-y-6">
        {grouped.map(([day, dayEvents]) => (
          <div key={day}>
            <div className="sticky top-0 z-10 -mx-4 sm:-mx-6 px-4 sm:px-6 py-1.5 bg-white/90 backdrop-blur border-b border-slate-100">
              <p className="text-[10px] uppercase tracking-widest font-bold text-slate-500">
                {day === "unknown" ? "Undated" : new Date(day).toLocaleDateString([], { weekday: "long", month: "short", day: "numeric", year: "numeric" })}
              </p>
            </div>
            <ol className="mt-3 space-y-3">
              {dayEvents.map((ev, i) => {
                const Icon = iconFor(ev);
                return (
                  <li key={`${ev.type}-${i}-${ev.at || ""}`} className="flex items-start gap-3" data-testid={`admin-timeline-event-${ev.type}`}>
                    <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center shrink-0 text-slate-600">
                      <Icon size={14} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-sm font-semibold text-slate-800">{ev.title}</p>
                        <span className="text-[11px] text-slate-400 whitespace-nowrap font-mono">
                          {ev.at ? new Date(ev.at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }) : ""}
                        </span>
                      </div>
                      {ev.subtitle && <p className="text-xs text-slate-600 mt-0.5 whitespace-pre-line line-clamp-3">{ev.subtitle}</p>}
                    </div>
                  </li>
                );
              })}
            </ol>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AdminTimeline;
